const API_BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/$/, "");
const apiUrl = (path: string) => `${API_BASE}${path}`;

export interface ExtractedSeries {
  label: string;
  confidence: number;
  points: { x: number | string; y: number }[];
}

export interface ExtractionResult {
  chart_type: string;
  title: string | null;
  x_axis: { label: string | null; units: string | null; scale: "linear" | "log" | "category" };
  y_axis: { label: string | null; units: string | null; scale: "linear" | "log" };
  series: ExtractedSeries[];
  notes: string | null;
  /** Pixel anchors used to map data-coords back onto the source image for the verification overlay. */
  anchors: {
    x_axis: { value: number | string; px: number }[];
    y_axis: { value: number; px: number }[];
  } | null;
}

export interface ExtractResponse {
  result: ExtractionResult;
  remaining_free: number | null;
  paid: boolean;
}

export interface ApiError {
  error: string;
  status: number;
  remaining_free?: number;
}

export async function extractChart(
  file: File,
  token: string | null
): Promise<ExtractResponse> {
  const form = new FormData();
  form.append("image", file);
  const res = await fetch(apiUrl("/api/extract"), {
    method: "POST",
    body: form,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const body: ApiError = await res.json().catch(() => ({
      error: "Network error",
      status: res.status,
    }));
    throw body;
  }
  return res.json();
}

export async function startCheckout(email: string): Promise<{ url: string }> {
  const res = await fetch(apiUrl("/api/checkout"), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function openBillingPortal(
  token: string,
  flow?: "cancel"
): Promise<{ url: string }> {
  const qs = flow ? `?flow=${flow}` : "";
  const res = await fetch(apiUrl(`/api/portal${qs}`), {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const body: ApiError = await res.json().catch(() => ({
      error: "Could not open billing portal",
      status: res.status,
    }));
    throw body;
  }
  return res.json();
}

export async function requestMagicLink(email: string): Promise<{ ok: true }> {
  const res = await fetch(apiUrl("/api/auth/magic-link"), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function consumeMagicLink(token: string): Promise<{ session: string; email: string }> {
  const res = await fetch(apiUrl(`/api/auth/consume?token=${encodeURIComponent(token)}`));
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}
